<template>
  <v-row>
    <v-col
      sm="3"
      class="hidden-xs-only"
    >
      <div style="position: sticky; top: 20px">
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.label"
          label="Label"
          solo
          clearable
          hide-details
          class="mb-2"
        />
        <v-text-field
          v-model="filtersDirty.code"
          label="Code"
          solo
          clearable
          hide-details
          class="mb-2"
        />
      </div>
    </v-col>
    <v-col
      cols="16"
      sm="13"
    >
      <pagination
        :from="inventoryItemsIssued.meta.from"
        :to="inventoryItemsIssued.meta.to"
        :total="inventoryItemsIssued.meta.total"
        :last-page="inventoryItemsIssued.meta.last_page"
        :page.sync="pagination.page"
        class="align-center mb-2"
      />
      <v-data-table
        :headers="inventoryItemsIssued.headers"
        :items="inventoryItemsIssued.data"
        :page.sync="pagination.page"
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.desc"
        :loading="inventoryItemsIssued.loading"
        :server-items-length="inventoryItemsIssued.meta.total"
        item-key="id"
        must-sort
        hide-default-footer
        class="elevation-5"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td class="font-weight-medium">
              {{ item.inventory_item.label }}
            </td>
            <td>
              <span
                v-if="item.inventory_item.code"
                class="grey--text"
              >
                {{ item.inventory_item.code }}
              </span>
              <span
                v-else
                class="grey--text"
              >
                n/a
              </span>
            </td>
            <td :class="{ 'error--text': !item.amount }">
              {{ item.amount | currency('') }}
              <span class="grey--text">
                {{ item.inventory_item.unit }}
              </span>
            </td>
            <td>
              {{ item.staff.name }}
            </td>
            <td class="text-no-wrap">
              <v-btn
                small
                text
                rounded
                color="grey"
                class="text-none ma-0"
                @click="openInventoryItemIssuedRevokeDialog(item)"
              >
                <v-icon
                  small
                  class="mr-1"
                >
                  fa fa-ban
                </v-icon>
                Revoke
              </v-btn>
            </td>
          </tr>
        </template>
        <template slot="no-data">
          <!--<editor-fold desc="Filtered">-->
          <div
            v-if="isFiltered"
            class="text-center py-4"
          >
            <div>
              There are no results for your search.
            </div>
            <div
              class="mt-2"
            >
              <v-btn
                small
                text
                @click="clearFilters"
              >
                <v-icon left>
                  clear
                </v-icon>
                Clear Filters
              </v-btn>
            </div>
          </div>
          <!--</editor-fold>-->
          <!--<editor-fold desc="NoData">-->
          <div
            v-else
            class="text-center py-4"
          >
            <v-col cols="16">
              There are issued inventory items at the moment.
            </v-col>
          </div>
          <!--</editor-fold>-->
        </template>
      </v-data-table>
      <pagination
        :from="inventoryItemsIssued.meta.from"
        :to="inventoryItemsIssued.meta.to"
        :total="inventoryItemsIssued.meta.total"
        :last-page="inventoryItemsIssued.meta.last_page"
        :page.sync="pagination.page"
        class="align-center mt-2"
      />
    </v-col>

    <inventory-issued-revoke-dialog
      v-if="inventoryItemIssuedDirty"
      :visible.sync="inventoryIssuedRevokeDialog"
      :inventory-item-issued="inventoryItemIssuedDirty"
      @inventoryItemIssued:revoked="fetchInventoryItemsIssued"
    />
  </v-row>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep'
  import serviceCurrentLocationMixin from '@/mixins/serviceCurrentLocationMixin'
  import paginationMixin from '@/mixins/paginationMixin'
  import filterableMixin from '@/mixins/filterableMixin'
  import InventoryItem from '@/models/InventoryItem'
  import InventoryIssuedRevokeDialog from '@/components/service/inventory/InventoryIssuedRevokeDialog'

  export default {
    name: 'InventoryItems',
    components: {
      InventoryIssuedRevokeDialog
    },
    mixins: [paginationMixin, filterableMixin, serviceCurrentLocationMixin],
    data() {
      return {
        inventoryItemsIssued: {
          headers: [
            { text: 'Label', value: 'label', sortable: false },
            { text: 'Code', value: 'code', sortable: false },
            { text: 'Amount', value: 'amount', sortable: false },
            { text: 'To', value: 'staff.name', sortable: false },
            { sortable: false, width: '1%' }
          ],
          data: [],
          loading: false,
          meta: {}
        },
        inventoryIssuedRevokeDialog: false,
        inventoryItemIssuedDirty: undefined,
        pagination: {
          perPage: 30
        },
        sorting: {
          sortBy: undefined,
          desc: undefined
        },
        filtersClean: cloneDeep(InventoryItem.emptyFilters),
        filtersDirty: cloneDeep(InventoryItem.emptyFilters)
      }
    },
    created() {
      this.fetchInventoryItemsIssued()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchInventoryItemsIssued()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      onCurrentLocationUpdate() {
        this.resetPagination()
      },
      async fetchInventoryItemsIssued() {
        this.inventoryItemsIssued.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams,
          ...this.currentLocationQueryParams
        }

        try {
          const result = await InventoryItem.api.getIssuedItems(queryParams)

          this.inventoryItemsIssued.data = result.data
          this.inventoryItemsIssued.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.inventoryItemsIssued.loading = false
        }
      },
      openInventoryItemIssuedRevokeDialog(inventoryItemIssued = undefined) {
        this.inventoryItemIssuedDirty = cloneDeep(inventoryItemIssued)
        this.inventoryIssuedRevokeDialog = true
      }
    }
  }
</script>
